// Deterministic replays of the inbox workflow for Lesson 4.1 ("One Prompt Cannot Do Three
// Jobs"). Every label, reason, title, duration, token count and judge sentence below was
// recorded from real runs on 8 October 2026. The graphics replay those recordings and never
// call a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const yesNo = (value: boolean) => (value ? 'yes' : 'no');
const count = (value: number, noun: string) => `${value} ${noun}${value === 1 ? '' : 's'}`;

// --- flow-route: the router ----------------------------------------------------------------

export const ROUTE_MESSAGES = ['note-1', 'question', 'command', 'thanks', 'mixed'] as const;
export type RouteMessage = (typeof ROUTE_MESSAGES)[number];
export const routeMessageLabels: Record<RouteMessage, string> = {
  'note-1': 'Note 1, the standup',
  question: 'Who owns the release note?',
  command: 'Assign the badges task to Ravi.',
  thanks: 'Thanks all, great week!',
  mixed: 'The demo is on the 12th, book the room',
};
export const toRouteMessage = (value: string): RouteMessage =>
  ROUTE_MESSAGES.find((message) => message === value) ?? 'note-1';
export const LABELS = ['note', 'question', 'command', 'other'] as const;
export type Label = (typeof LABELS)[number];
export interface RoutedMessage {
  text: string;
  label: Label;
  reason: string;
}
export const NOTE_1 =
  'Apollo standup, 3 March. Demo is on the 12th. We still need a room for it. Ravi asked for the agenda by Friday. Jo thinks the projector in the small room is broken, someone should check it. Badges are printed.';
/** The five messages with the label and reason the router returned for each. */
export const routedMessages: Record<RouteMessage, RoutedMessage> = {
  'note-1': {
    text: NOTE_1,
    label: 'note',
    reason:
      'The message lists meeting notes and status updates regarding a standup, including deadlines, outstanding tasks, and completed items.',
  },
  question: {
    text: 'Who owns the release note?',
    label: 'question',
    reason: 'The message is asking for specific information regarding the ownership of a document.',
  },
  command: {
    text: 'Assign the badges task to Ravi.',
    label: 'command',
    reason: 'The message is a directive asking CollabBoard to assign a specific task to a person.',
  },
  thanks: {
    text: 'Thanks all, great week!',
    label: 'other',
    reason:
      "The message is a closing greeting expressing appreciation for the week's work without requesting information or action.",
  },
  mixed: {
    text: 'The demo is on the 12th, book the room',
    label: 'command',
    reason: 'The message instructs CollabBoard to perform an action, specifically to book a room.',
  },
};
/** The code path the switch on the label takes. */
export const nextSteps: Record<Label, string> = {
  note: 'extract tasks',
  question: 'answer, not yet',
  command: 'act, not yet',
  other: 'nothing',
};
export interface RouteState {
  message: RouteMessage;
  classified: boolean;
}
export const initialRoute = (): RouteState => ({ message: 'note-1', classified: false });
export type RouteAction =
  { type: 'message'; value: RouteMessage } | { type: 'classify' } | { type: 'reset' };
export function routeTransition(state: RouteState, action: RouteAction): RouteState {
  switch (action.type) {
    case 'reset':
      return initialRoute();
    case 'message':
      return { message: action.value, classified: false };
    case 'classify':
      return { ...state, classified: true };
  }
}
export const ROUTE_START_MESSAGE = 'Choose a message, then press Classify.';
export const routeResults: Record<Label, string> = {
  note: 'Labeled note, so the chain extracts tasks next.',
  question: 'Labeled question, so the answer path runs next. It is not built yet.',
  command: 'Labeled command, so the act path runs next. It is not built yet.',
  other: 'Labeled other, so nothing runs.',
};
export const ROUTE_MIXED_MESSAGE = 'Labeled command, so its note content is never extracted.';
export const ROUTE_REFINEMENT =
  'Refinement: a labeled evaluation set, and an other label that asks instead of guessing.';
export function routeSummary(state: RouteState): Summary {
  if (!state.classified) return { tone: 'neutral', message: ROUTE_START_MESSAGE };
  if (state.message === 'mixed') return { tone: 'warning', message: ROUTE_MIXED_MESSAGE };
  const label = routedMessages[state.message].label;
  return { tone: label === 'other' ? 'neutral' : 'success', message: routeResults[label] };
}

// --- flow-chain: extract, compare, propose ---------------------------------------------------

export const CHAIN_NOTES = ['1', '4', '3'] as const;
export type ChainNote = (typeof CHAIN_NOTES)[number];
export const chainNoteLabels: Record<ChainNote, string> = {
  '1': 'Note 1, the standup',
  '4': 'Note 4, the planning call reminder',
  '3': 'Note 3, the thank-you note',
};
export const toChainNote = (value: string): ChainNote =>
  CHAIN_NOTES.find((note) => note === value) ?? '1';
export const chainNoteTexts: Record<ChainNote, string> = {
  '1': NOTE_1,
  '4': 'Reminder from the planning call: Ravi still owns the release note for Apollo. Nothing else is new.',
  '3': 'Thanks everyone for a great week. The release note went out and the feedback was good. Enjoy the weekend!',
};
/** The open titles of Apollo: the compare step reads open tasks only, as the inbox code does. */
export const EXISTING_TASKS: readonly string[] = [
  'Write the release note',
  'Check the dates',
  'Order ten more name badges',
  'Check the projector in the small room',
  'Send the final headcount to the venue',
  'Write the release note for Apollo',
];
/** What the extraction call returned for each note, sent without the existing tasks. */
export const extractedTitles: Record<ChainNote, readonly string[]> = {
  '1': [
    'Book a room for the demo',
    'Send agenda to Ravi by Friday',
    'Check if the projector in the small room is broken',
  ],
  '4': ['Write the release note for Apollo'],
  '3': [],
};
/** The compare step's verdicts: the existing task each extracted title duplicates. */
export const duplicateOf: Readonly<Record<string, string>> = {
  'Write the release note for Apollo': 'Write the release note',
  'Check if the projector in the small room is broken': 'Check the projector in the small room',
};
export interface ChainRun {
  extracted: string[];
  /** Each dropped title with the existing task it duplicates. */
  dropped: [string, string][];
  kept: string[];
  proposals: string[];
}
export function runChain(note: ChainNote, compareOn: boolean): ChainRun {
  const extracted = [...extractedTitles[note]];
  const dropped: [string, string][] = [];
  const kept: string[] = [];
  for (const title of extracted) {
    const existing = compareOn ? duplicateOf[title] : undefined;
    if (existing === undefined) kept.push(title);
    else dropped.push([title, existing]);
  }
  return { extracted, dropped, kept, proposals: [...kept] };
}
export interface ChainStep {
  step: string;
  who: string;
  input: string;
  output: string;
}
const list = (titles: readonly string[], empty: string) =>
  titles.length ? titles.join(', ') : empty;
export function chainSteps(run: ChainRun, compareOn: boolean): ChainStep[] {
  const compared = run.extracted.length
    ? compareOn
      ? `dropped ${count(run.dropped.length, 'duplicate')}, kept ${run.kept.length}`
      : `skipped, kept ${run.kept.length}`
    : 'nothing to compare';
  return [
    { step: 'extract', who: 'model', input: 'the note', output: list(run.extracted, 'no tasks') },
    {
      step: 'compare',
      who: compareOn ? 'code' : 'skipped',
      input: `${count(run.extracted.length, 'title')} and ${EXISTING_TASKS.length} existing tasks`,
      output: compared,
    },
    {
      step: 'propose',
      who: 'code',
      input: count(run.kept.length, 'title'),
      output: list(run.proposals, 'no proposals'),
    },
  ];
}
export const droppedLines = (run: ChainRun) =>
  run.dropped.length
    ? run.dropped.map(([title, existing]) => `${title} → ${existing}`).join('\n')
    : '(none)';
export interface ChainState {
  note: ChainNote;
  compareOn: boolean;
  showExisting: boolean;
  run: ChainRun | null;
}
export const initialChain = (): ChainState => ({
  note: '1',
  compareOn: true,
  showExisting: false,
  run: null,
});
export type ChainAction =
  | { type: 'note'; value: ChainNote }
  | { type: 'toggle-compare' }
  | { type: 'toggle-existing' }
  | { type: 'run' }
  | { type: 'reset' };
export function chainTransition(state: ChainState, action: ChainAction): ChainState {
  switch (action.type) {
    case 'reset':
      return initialChain();
    case 'note':
      return { ...state, note: action.value, run: null };
    case 'toggle-compare':
      return { ...state, compareOn: !state.compareOn, run: null };
    case 'toggle-existing':
      return { ...state, showExisting: !state.showExisting };
    case 'run':
      return { ...state, run: runChain(state.note, state.compareOn) };
  }
}
export const CHAIN_START_MESSAGE = 'Choose a note, then press Run the chain.';
export const CHAIN_NOTHING_MESSAGE =
  'The model extracted nothing, so there is nothing to compare or propose.';
export const CHAIN_DROPPED_ALL_MESSAGE =
  'The compare step dropped the duplicate, so the chain proposes nothing.';
export const CHAIN_DROPPED_SOME_MESSAGE =
  'The compare step dropped one duplicate and kept two new tasks.';
export const chainWithoutCompareMessage = (duplicates: number) =>
  `Without the code step ${count(duplicates, 'duplicate')} reached the proposals.`;
export const CHAIN_REFINEMENT = 'Refinement: the deterministic check is a code step with a test.';
export function chainSummary(state: ChainState): Summary {
  const run = state.run;
  if (!run) return { tone: 'neutral', message: CHAIN_START_MESSAGE };
  if (!run.extracted.length) return { tone: 'neutral', message: CHAIN_NOTHING_MESSAGE };
  if (!state.compareOn) {
    const missed = run.extracted.filter((title) => duplicateOf[title] !== undefined).length;
    return { tone: 'warning', message: chainWithoutCompareMessage(missed) };
  }
  return {
    tone: 'success',
    message: run.kept.length ? CHAIN_DROPPED_SOME_MESSAGE : CHAIN_DROPPED_ALL_MESSAGE,
  };
}

// --- flow-parallel: two calls in two threads -------------------------------------------------

export const PARALLEL_MODES = ['sequential', 'parallel'] as const;
export type ParallelMode = (typeof PARALLEL_MODES)[number];
export const parallelModeLabels: Record<ParallelMode, string> = {
  sequential: 'One after another',
  parallel: 'Together',
};
export const toParallelMode = (value: string): ParallelMode =>
  PARALLEL_MODES.find((mode) => mode === value) ?? 'sequential';
/** The two calls as the sequential run recorded them. */
export const DIGEST = { name: 'Digest', seconds: 1.54, tokens: 131 } as const;
export const EXTRACT = { name: 'Extraction', seconds: 1.22, tokens: 173 } as const;
export const SEQUENTIAL_SECONDS = 2.75;
export const PARALLEL_SECONDS = 1.54;
export const TOTAL_TOKENS = DIGEST.tokens + EXTRACT.tokens;
export const CALLS = 2;
/** Seconds across the full width of the timeline. */
export const TIMELINE_SCALE = 2.9;
export interface ParallelState {
  mode: ParallelMode;
  compareOn: boolean;
}
export const initialParallel = (): ParallelState => ({ mode: 'sequential', compareOn: false });
export type ParallelAction =
  { type: 'mode'; value: ParallelMode } | { type: 'toggle-compare' } | { type: 'reset' };
export function parallelTransition(state: ParallelState, action: ParallelAction): ParallelState {
  switch (action.type) {
    case 'reset':
      return initialParallel();
    case 'mode':
      return { ...state, mode: action.value };
    case 'toggle-compare':
      return { ...state, compareOn: !state.compareOn };
  }
}
export interface TimelineBar {
  name: string;
  start: number;
  seconds: number;
  text: string;
}
export function timelineBars(state: ParallelState): TimelineBar[] {
  const extractStart = state.mode === 'parallel' ? 0 : DIGEST.seconds;
  const bars: TimelineBar[] = [
    { name: DIGEST.name, start: 0, seconds: DIGEST.seconds, text: `${DIGEST.seconds} s` },
    {
      name: EXTRACT.name,
      start: extractStart,
      seconds: EXTRACT.seconds,
      text: `${EXTRACT.seconds} s`,
    },
  ];
  if (state.compareOn)
    bars.push({
      name: 'Compare (code)',
      start: extractStart + EXTRACT.seconds,
      seconds: 0.06,
      text: 'waits for the extraction',
    });
  return bars;
}
const percent = (seconds: number) => `${Math.round((seconds / TIMELINE_SCALE) * 1000) / 10}%`;
export const barLayout = (bar: TimelineBar) => ({
  marginLeft: percent(bar.start),
  width: percent(bar.seconds),
});
export const totalSeconds = (state: ParallelState) =>
  state.mode === 'parallel' ? PARALLEL_SECONDS : SEQUENTIAL_SECONDS;
export const tokensLine = `${TOTAL_TOKENS} (${DIGEST.tokens} + ${EXTRACT.tokens})`;
export const PARALLEL_SEQUENTIAL_MESSAGE = `One after another takes ${SEQUENTIAL_SECONDS} s, the two durations added.`;
export const PARALLEL_TOGETHER_MESSAGE = `Together takes ${PARALLEL_SECONDS} s, the slower call, and still ${TOTAL_TOKENS} tokens.`;
export const PARALLEL_COMPARE_MESSAGE =
  'Compare needs the extraction output, so it waits and the total does not change.';
export const PARALLEL_REFINEMENT = 'Refinement: run only independent steps together.';
export function parallelSummary(state: ParallelState): Summary {
  if (state.compareOn) return { tone: 'warning', message: PARALLEL_COMPARE_MESSAGE };
  if (state.mode === 'parallel') return { tone: 'success', message: PARALLEL_TOGETHER_MESSAGE };
  return { tone: 'neutral', message: PARALLEL_SEQUENTIAL_MESSAGE };
}

// --- flow-check: code checks, then the judge -------------------------------------------------

export const CHECK_PROPOSALS = ['good', 'sam', 'long'] as const;
export type CheckProposal = (typeof CHECK_PROPOSALS)[number];
export const GOOD_TITLE = 'Order ten more name badges for the partners';
export const LONG_TITLE =
  'Order ten more name badges for the partners before the 10th so that everyone registered has one';
export const TITLE_LIMIT = 80;
export const MEMBERS: readonly string[] = ['Maya', 'Ravi', 'Jo'];
export const checkProposalLabels: Record<CheckProposal, string> = {
  good: GOOD_TITLE,
  sam: 'The same title, assigned to Sam',
  long: `A ${LONG_TITLE.length}-character title`,
};
export const toCheckProposal = (value: string): CheckProposal =>
  CHECK_PROPOSALS.find((proposal) => proposal === value) ?? 'good';
export interface Proposal {
  title: string;
  assignee: string | null;
}
export const checkProposals: Record<CheckProposal, Proposal> = {
  good: { title: GOOD_TITLE, assignee: null },
  sam: { title: GOOD_TITLE, assignee: 'Sam' },
  long: { title: LONG_TITLE, assignee: null },
};
export const proposalJson = (proposal: Proposal) =>
  `{"title": "${proposal.title}", "assignee": ${proposal.assignee === null ? 'null' : `"${proposal.assignee}"`}}`;
export type CodeVerdict = 'ok' | 'assignee not a member' | 'title too long';
/** The rules code checks before any call: the title limit, then membership. */
export function codeCheck(proposal: Proposal): CodeVerdict {
  if (proposal.title.length > TITLE_LIMIT) return 'title too long';
  if (proposal.assignee !== null && !MEMBERS.includes(proposal.assignee))
    return 'assignee not a member';
  return 'ok';
}
export const codeVerdictText = (verdict: CodeVerdict | 'skipped', proposal: Proposal) =>
  verdict === 'title too long'
    ? `title too long (${proposal.title.length} characters, the limit is ${TITLE_LIMIT})`
    : verdict === 'assignee not a member'
      ? `assignee not a member (${proposal.assignee ?? ''} is not in Apollo)`
      : verdict;
export interface JudgeReply {
  verdict: 'pass' | 'fail';
  critique: string;
}
/** What the judge answered for each proposal. */
export const judgeReplies: Record<CheckProposal, JudgeReply> = {
  good: {
    verdict: 'pass',
    critique:
      "The proposal follows directly from the note's instruction to order ten more name badges for partners.",
  },
  sam: {
    verdict: 'fail',
    critique:
      'Sam is not a member of the Apollo project team, so the task cannot be assigned to them.',
  },
  long: {
    verdict: 'pass',
    critique:
      "The task follows directly from the note's mention that partners may attend and that someone needs to order ten more name badges.",
  },
};
export const judgeText = (judge: JudgeReply | null) =>
  judge ? `${judge.verdict}: ${judge.critique}` : '(not called)';
export interface CheckRun {
  code: CodeVerdict | 'skipped';
  judge: JudgeReply | null;
  judgeCalls: 0 | 1;
  retried: boolean;
  outcome: string;
}
export const REFUSED_OUTCOME = 'refused by code';
export const WRITTEN_OUTCOME = 'written as a proposal';
export const DOUBTS_OUTCOME = 'sent to Maya with the critique';
/** The checker: rules first when the toggle is on, then the judge, then at most one retry. */
export function runCheck(proposal: CheckProposal, codeFirst: boolean): CheckRun {
  const code = codeFirst ? codeCheck(checkProposals[proposal]) : 'skipped';
  if (code !== 'ok' && code !== 'skipped')
    return { code, judge: null, judgeCalls: 0, retried: false, outcome: REFUSED_OUTCOME };
  const judge = judgeReplies[proposal];
  if (judge.verdict === 'fail')
    return { code, judge, judgeCalls: 1, retried: true, outcome: DOUBTS_OUTCOME };
  return { code, judge, judgeCalls: 1, retried: false, outcome: WRITTEN_OUTCOME };
}
export const retryLabel = (retried: boolean) => (retried ? 'one, then Maya decides' : 'none');
export interface CheckState {
  proposal: CheckProposal;
  codeFirst: boolean;
  run: CheckRun | null;
}
export const initialCheck = (): CheckState => ({ proposal: 'good', codeFirst: true, run: null });
export type CheckAction =
  | { type: 'proposal'; value: CheckProposal }
  | { type: 'toggle-code-first' }
  | { type: 'check' }
  | { type: 'reset' };
export function checkTransition(state: CheckState, action: CheckAction): CheckState {
  switch (action.type) {
    case 'reset':
      return initialCheck();
    case 'proposal':
      return { ...state, proposal: action.value, run: null };
    case 'toggle-code-first':
      return { ...state, codeFirst: !state.codeFirst, run: null };
    case 'check':
      return { ...state, run: runCheck(state.proposal, state.codeFirst) };
  }
}
export const CHECK_START_MESSAGE = 'Choose a proposal, then press Check.';
export const CHECK_GOOD_MESSAGE =
  'Code found no rule broken and the judge passed the meaning for 1 call.';
export const CHECK_GOOD_ALONE_MESSAGE =
  'The judge passed it for 1 call, but no rule was checked first.';
export const CHECK_SAM_CODE_MESSAGE = 'Code refused Sam before the judge, so no call was spent.';
export const CHECK_SAM_JUDGE_MESSAGE = 'The judge caught Sam for 1 call, then one retry ran.';
export const CHECK_LONG_CODE_MESSAGE = 'Code refused the long title, so the judge cost 0 calls.';
export const CHECK_LONG_JUDGE_MESSAGE =
  'The judge passed the long title for 1 call, so a bad title went through.';
export const CHECK_REFINEMENT =
  'Refinement: code first for rules, the judge for meaning, one retry.';
export function checkSummary(state: CheckState): Summary {
  if (!state.run) return { tone: 'neutral', message: CHECK_START_MESSAGE };
  switch (state.proposal) {
    case 'good':
      return state.codeFirst
        ? { tone: 'success', message: CHECK_GOOD_MESSAGE }
        : { tone: 'neutral', message: CHECK_GOOD_ALONE_MESSAGE };
    case 'sam':
      return state.codeFirst
        ? { tone: 'success', message: CHECK_SAM_CODE_MESSAGE }
        : { tone: 'neutral', message: CHECK_SAM_JUDGE_MESSAGE };
    case 'long':
      return state.codeFirst
        ? { tone: 'success', message: CHECK_LONG_CODE_MESSAGE }
        : { tone: 'warning', message: CHECK_LONG_JUDGE_MESSAGE };
  }
}
